import React, { useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { FaStar } from "react-icons/fa";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Oleo+Script:wght@400;700&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
  body { margin:0; padding:0; background:transparent; font-family: 'Playfair Display', serif; }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(3deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const twinkle = keyframes`
  0% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.18); }
  100% { opacity: 0.4; transform: scale(1); }
`;

const burst = keyframes`
  0% { transform: scale(0.6); opacity: 0.85; }
  60% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0; }
`;

const swing = keyframes`
  0% { transform: rotate(-6deg); }
  50% { transform: rotate(6deg); }
  100% { transform: rotate(-6deg); }
`;

const ParallaxContainer = styled.div`
  perspective: 900px;
  overflow: hidden;
  `;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: contents;
  transform-style: preserve-3d;
`;

const Header = styled.header`
  position: relative;
  padding: 3rem 2rem 6rem;
  background: linear-gradient(135deg, #2b0b3a 0%, #3b0f5a 40%, #6b1f18 100%);
  color: #fff5e6;
  overflow: hidden;
  border-radius: 0;
  box-shadow: 0 12px 40px rgba(0,0,0,0.45);
`;

const Title = styled.h1`
  font-size: 3.2rem;
  margin: 1rem 0;
  color: #ffd166;
  font-family: 'Oleo Script', cursive;
  text-shadow: 0 6px 18px rgba(255,160,50,0.15);
  transform: translateZ(30px);
`;

const Subtitle = styled.h2`
  font-size: 1.3rem;
  color: #ffd9b3;
  font-weight: 400;
  transform: translateZ(25px);
`;

const Spark = styled(FaStar)`
  position: absolute;
  color: ${props => props.color || "#ffd166"};
  font-size: ${props => props.size || "1.4rem"};
  top: ${props => props.top || "10%"};
  left: ${props => props.left || "10%"};
  animation: ${twinkle} ${props => props.duration || "2s"} infinite;
  transform: translateZ(${props => props.depth || 20}px);
  opacity: 0.9;
`;

const DiyaContainer = styled.div`
  position: absolute;
  bottom: ${props => props.bottom || "40px"};
  ${props => props.left ? `left: ${props.left};` : ''}
  ${props => props.right ? `right: ${props.right};` : ''}
  transform-origin: top center;
  animation: ${swing} ${props => props.swingDuration || "5.5s"} ease-in-out infinite;
  z-index: 6;
`;

const Diya = styled.div`
  position: relative;
  width: 56px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateZ(40px);

  .bowl {
    width: 56px;
    height: 20px;
    background: linear-gradient(180deg,#8a2b10,#d97706);
    border-radius: 0 0 20px 20px;
    box-shadow: 0 6px 14px rgba(0,0,0,0.35);
    position: absolute;
    bottom: 0;
    left: 0;
    overflow: hidden;
  }

  .wick {
    width: 6px;
    height: 12px;
    background: #3b2a20;
    position: absolute;
    bottom: 18px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }

  .flame {
    position: absolute;
    bottom: 26px;
    left: 50%;
    transform: translateX(-50%);
    width: 16px;
    height: 24px;
    background: radial-gradient(circle at 50% 35%, #fff1b8 0%, #ffd166 25%, #ff6b35 70%);
    clip-path: polygon(50% 0%, 64% 22%, 82% 40%, 50% 100%, 18% 40%, 36% 22%);
    filter: drop-shadow(0 4px 10px rgba(255,120,30,0.35));
    animation: ${float} 3.6s ease-in-out infinite;
  }
`;

const Burst = styled.div`
  position: absolute;
  width: ${props => props.size || "120px"};
  height: ${props => props.size || "120px"};
  top: ${props => props.top || "20px"};
  left: ${props => props.left || "50%"};
  transform: translateX(-50%) translateZ(30px);
  pointer-events: none;
  svg { width:100%; height:100%; }
  animation: ${burst} 1.6s ease-out ${props => props.delay || "0s"} forwards;
`;

const Rangoli = styled.div`
  width: 100%;
  max-width: 700px;
  margin: 2rem auto 0;
  transform: translateZ(25px);
  display: flex;
  justify-content: center;
`;

const DiyaRow = styled.div`
  display:flex;
  justify-content: center;
  gap: 18px;
  margin-top: 20px;
  transform: translateZ(30px);
`;

const DiwaliButton = styled.button`
  background: linear-gradient(90deg,#ff9b3d,#ffd166);
  border: none;
  color: #3b0b1e;
  padding: 0.9rem 1.6rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 1rem;
  box-shadow: 0 10px 30px rgba(255,140,50,0.12);
  transition: transform .18s ease;
  &:hover { transform: translateY(-4px); }
`;

const DiwaliNewTheme = () => {
  const [bursts] = useState([
    { id: 1, top: "18%", left: "12%", size: "80px", delay: "0s", color: "#ffd166" },
    { id: 2, top: "8%", left: "72%", size: "140px", delay: "0.2s", color: "#ff6b35" },
    { id: 3, top: "28%", left: "50%", size: "100px", delay: "0.4s", color: "#ffb86b" }
  ]);

  const stars = Array.from({ length: 18 }).map((_, i) => ({
    id: i,
    size: `${Math.random() * 1.6 + 0.6}rem`,
    top: `${Math.random() * 55 + 6}%`,
    left: `${Math.random() * 85 + 4}%`,
    duration: `${1.6 + Math.random() * 2}s`,
    depth: Math.floor(Math.random() * 40) + 12,
    color: i % 2 ? "#ffd166" : "#ffb86b"
  }));

  return (
    <>
      <GlobalStyle />
      <ParallaxContainer>
        <Container>
          <Header>
            {stars.map(s => (
              <Spark
                key={s.id}
                style={{ top: s.top, left: s.left, fontSize: s.size, animationDelay: `${Math.random()*2}s` }}
                duration={s.duration}
                depth={s.depth}
                color={s.color}
              />
            ))}

            <Title>Happy Diwali</Title>
            <Subtitle>May the light in your life win over darkness and bring you joy</Subtitle>

            <DiyaContainer left="6%" bottom="54px" swingDuration="5.6s">
              <Diya>
                <div className="bowl" />
                <div className="wick" />
                <div className="flame" />
              </Diya>
            </DiyaContainer>

            <DiyaContainer left="26%" bottom="36px" swingDuration="6.2s">
              <Diya>
                <div className="bowl" />
                <div className="wick" />
                <div className="flame" />
              </Diya>
            </DiyaContainer>

            <DiyaContainer right="18%" bottom="42px" swingDuration="5.1s">
              <Diya>
                <div className="bowl" />
                <div className="wick" />
                <div className="flame" />
              </Diya>
            </DiyaContainer>

            {bursts.map(b => (
              <Burst key={b.id} top={b.top} left={b.left} size={b.size} delay={b.delay}>
                <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
                  <g transform="translate(50,50)">
                    <circle r="2" fill={b.color} opacity="0.95" />
                    {[...Array(10)].map((_,i) => (
                      <line key={i} x1="0" y1="0" x2={`${Math.cos((i/10)*Math.PI*2)*40}`} y2={`${Math.sin((i/10)*Math.PI*2)*40}`} stroke={b.color} strokeWidth="1.6" strokeLinecap="round" opacity="0.95" />
                    ))}
                  </g>
                </svg>
              </Burst>
            ))}

            <Rangoli>
              <svg viewBox="0 0 220 220" width="220" height="220" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <radialGradient id="rg" cx="50%" cy="40%">
                    <stop offset="0%" stopColor="#fff8e6" stopOpacity="0.95" />
                    <stop offset="60%" stopColor="#ffd9b3" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ff934f" stopOpacity="0.85" />
                  </radialGradient>
                </defs>
                <g transform="translate(110,110)">
                  <circle r="36" fill="url(#rg)" stroke="#ff6b35" strokeWidth="3" />
                  {[...Array(8)].map((_,i)=>(
                    <path key={i} d={`M0 ${-60} L12 ${-20} A40 40 0 0 1 -12 ${-20} Z`} fill={i%2? "#ffd166":"#ffb86b"} transform={`rotate(${i*45})`} opacity="0.95"/>
                  ))}
                  <circle r="8" fill="#fff8e6" />
                </g>
              </svg>
            </Rangoli>

            
          </Header>

         
        </Container>
      </ParallaxContainer>
    </>
  );
};

export default DiwaliNewTheme;